import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { selectLanguage } from '../../store/languageSlice';
import { cvData } from '../../data/cvData';
import { translations } from '../../data/translations';
import { HiMail, HiPhone, HiLocationMarker } from 'react-icons/hi';
import { revealStagger, revealUp } from '../../utils/gsapReveal';
import CreditsModal from '../Credits/Credits';
import './Footer.css';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const language = useSelector(selectLanguage);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const sectionRef = useRef(null);
  const data = cvData[language];
  const t = translations[language];
  const { personal } = data;

  useLayoutEffect(() => {
    const scope = sectionRef.current;
    if (!scope) return;

    const ctx = gsap.context(() => {
      revealUp(scope.querySelector('.footer__intro'), { y: 36 });
      revealUp(scope.querySelector('.footer__contacts-title'), { y: 28 });

      const contactList = scope.querySelector('.footer__contacts');
      if (contactList) {
        revealStagger(contactList, '.footer__contact-item', {
          y: 24,
          stagger: 0.1,
          start: 'top 85%',
        });
      }

      revealUp(scope.querySelector('.footer__bottom'), { y: 20, duration: 0.5 });
    }, scope);

    return () => ctx.revert();
  }, [language]);

  const handleEmailClick = () => {
    window.location.href = `mailto:${personal.email}`;
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${personal.phone}`;
  };

  return (
    <footer id="footer" className="footer" ref={sectionRef}>
      <div className="footer__inner">
        <div className="footer__grid">
          <div className="footer__intro">
            <h3 className="footer__name">{personal.name}</h3>
            <p className="footer__title">{personal.title}</p>
            <p className="footer__tagline">
              {language === 'it'
                ? 'Sempre disponibile per nuove opportunità e collaborazioni interessanti.'
                : 'Always available for new opportunities and interesting collaborations.'}
            </p>
          </div>

          <div>
            <h4 className="footer__contacts-title">
              {language === 'it' ? 'Contatti' : 'Contacts'}
            </h4>

            <div className="footer__contacts">
              <div className="footer__contact-item">
                <HiMail className="footer__contact-icon" aria-hidden />
                <button type="button" onClick={handleEmailClick} className="footer__contact-btn">
                  {personal.email}
                </button>
              </div>

              <div className="footer__contact-item">
                <HiPhone className="footer__contact-icon" aria-hidden />
                <button type="button" onClick={handlePhoneClick} className="footer__contact-btn">
                  {personal.phone}
                </button>
              </div>

              <div className="footer__contact-item">
                <HiLocationMarker className="footer__contact-icon" aria-hidden />
                <span className="footer__contact-text">{personal.city}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__bottom-line">
            © 2026 {personal.name}. {t.footer.rights}.
          </p>
          <p className="footer__bottom-accent">
            {language === 'it' ? 'Sviluppato con React + Redux' : 'Built with React + Redux'}
          </p>
          <p className="footer__bottom-line">
            <button
              type="button"
              onClick={() => setCreditsOpen(true)}
              className="footer__credits-btn"
            >
              Credits
            </button>
          </p>

          <CreditsModal isOpen={creditsOpen} onClose={() => setCreditsOpen(false)} />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
